import { useLocation, Link, Navigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Home, Rocket } from "lucide-react";

// Generate random stars for the background
const generateStars = (count: number) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 3 + 2,
    delay: Math.random() * 2,
  }));
};

// Floating planet component
const FloatingPlanet = ({ className }: { className?: string }) => (
  <motion.div
    className={className}
    animate={{
      y: [0, -20, 0],
      rotate: [0, 5, 0],
    }}
    transition={{
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  >
    <div className="relative">
      {/* Planet body */}
      <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-primary/80 via-primary to-primary/60 shadow-2xl shadow-primary/30">
        {/* Planet rings */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-40 md:w-52 h-6 md:h-8 border-2 border-primary/40 rounded-full transform -rotate-12 opacity-60" />
        </div>
        {/* Surface details */}
        <div className="absolute top-4 left-6 w-4 h-4 rounded-full bg-primary-foreground/20" />
        <div className="absolute top-8 right-4 w-6 h-6 rounded-full bg-primary-foreground/10" />
        <div className="absolute bottom-6 left-8 w-3 h-3 rounded-full bg-primary-foreground/15" />
      </div>
      {/* Glow effect */}
      <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl -z-10 scale-150" />
    </div>
  </motion.div>
);

// Floating astronaut/rocket component
const FloatingRocket = () => (
  <motion.div
    className="absolute"
    initial={{ x: -100, y: 100, rotate: -45 }}
    animate={{
      x: [null, 50, 150],
      y: [null, -50, -150],
      rotate: [-45, -30, -45],
    }}
    transition={{
      duration: 15,
      repeat: Infinity,
      ease: "linear",
    }}
  >
    <Rocket className="w-8 h-8 text-muted-foreground/40" />
  </motion.div>
);

// Star component with twinkling animation
const Star = ({ x, y, size, duration, delay }: { x: number; y: number; size: number; duration: number; delay: number }) => (
  <motion.div
    className="absolute rounded-full bg-foreground/80"
    style={{
      left: `${x}%`,
      top: `${y}%`,
      width: size,
      height: size,
    }}
    animate={{
      opacity: [0.2, 1, 0.2],
      scale: [1, 1.2, 1],
    }}
    transition={{
      duration,
      repeat: Infinity,
      delay,
      ease: "easeInOut",
    }}
  />
);

// Shooting star component
const ShootingStar = () => (
  <motion.div
    className="absolute w-1 h-1 bg-foreground rounded-full"
    style={{
      top: `${Math.random() * 50}%`,
      left: `${Math.random() * 50}%`,
    }}
    initial={{ x: 0, y: 0, opacity: 1 }}
    animate={{
      x: [0, 200],
      y: [0, 100],
      opacity: [1, 0],
    }}
    transition={{
      duration: 1.5,
      repeat: Infinity,
      repeatDelay: Math.random() * 8 + 4,
      ease: "easeOut",
    }}
  >
    {/* Trail */}
    <div className="absolute w-20 h-0.5 bg-gradient-to-r from-foreground/60 to-transparent -translate-x-full" />
  </motion.div>
);

const NotFound = () => {
  const location = useLocation();
  const stars = useMemo(() => generateStars(80), []);

  // Log the 404 error
  useEffect(() => {
    const decodedPath = decodeURIComponent(location.pathname);
    if (!decodedPath.includes('?') || decodedPath === location.pathname) {
      console.error(
        "404 Error: User attempted to access non-existent route:",
        location.pathname
      );
    }
  }, [location.pathname]);

  // Handle URL-encoded characters redirect
  const decodedPath = decodeURIComponent(location.pathname);
  if (decodedPath.includes('?') && decodedPath !== location.pathname) {
    return <Navigate to={decodedPath} replace />;
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-background via-background to-muted/20">
      {/* Starfield background */}
      <div className="absolute inset-0">
        {stars.map((star) => (
          <Star key={star.id} {...star} />
        ))}
        {/* Shooting stars */}
        <ShootingStar />
        <ShootingStar />
      </div>

      {/* Nebula/gradient overlays */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-accent/5 rounded-full blur-3xl" />
      </div>

      {/* Floating rocket */}
      <FloatingRocket />

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4">
        {/* Floating planet */}
        <FloatingPlanet className="mb-8" />

        {/* 404 text with glow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative"
        >
          <h1 className="text-8xl md:text-9xl font-bold text-foreground/10 select-none">
            404
          </h1>
          <motion.h1
            className="absolute inset-0 text-8xl md:text-9xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent"
            animate={{
              backgroundPosition: ["0%", "100%", "0%"],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            404
          </motion.h1>
        </motion.div>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-6 mb-8"
        >
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">
            Zagubiony w kosmosie
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Strona, której szukasz, nie istnieje lub została przeniesiona w inną galaktykę.
          </p>
        </motion.div>

        {/* Action button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <Button asChild size="lg" className="gap-2 group">
            <Link to="/panel">
              <Home className="w-5 h-5 transition-transform group-hover:-translate-y-0.5" />
              Wróć do panelu
            </Link>
          </Button>
        </motion.div>

        {/* Subtle orbit paths */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-muted/20 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-muted/10 rounded-full"
            animate={{ rotate: -360 }}
            transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </div>
    </div>
  );
};

export default NotFound;
